import { Button, Input, Select, Text, toast } from "@medusajs/ui"
import { useEffect, useMemo, useState } from "react"
import { Field } from "@/components/form"
import { errorMessage } from "@/lib/sdk"
import { useSaveShippingDefaults, useShippingDefaults, type PackageSize, type ShippingPrefs, type ShippingRule } from "@/lib/shipstation"
import { Banner } from "@/terabizz"

const ANY = "*"
const DEFAULT_FROM = "__default"
const svcKey = (r: Pick<ShippingRule, "carrier_code" | "service_code">) => `${r.carrier_code}::${r.service_code}`

/**
 * What every order arrives in ShipStation with, so nobody picks it per order: the service for
 * each checkout shipping choice, the ship-from location, and the mailer/box size per SKU (sent
 * when the order is that one item). Carriers and locations come from the ShipStation account.
 */
export function ShipStationShippingDefaults({ manage }: { manage: boolean }) {
  const { data, error, isLoading } = useShippingDefaults(true)
  const save = useSaveShippingDefaults()
  const d = data?.shipping
  const [p, setP] = useState<ShippingPrefs | null>(null)
  useEffect(() => {
    if (d) setP(d.prefs)
  }, [d])
  const services = useMemo(
    () => (d?.options?.carriers ?? []).flatMap((c) => c.services.filter((s) => s.domestic).map((s) => ({ key: svcKey({ carrier_code: c.code, service_code: s.code }), carrier: c, service: s, label: `${c.name} · ${s.name}` }))),
    [d],
  )
  if (error) return <Text size="small" className="px-4 pb-3 text-ui-fg-error">{errorMessage(error)}</Text>
  if (isLoading || !d || !p) return <Text size="small" className="px-4 pb-3 text-ui-fg-subtle">Loading shipping defaults…</Text>

  const dirty = JSON.stringify(p) !== JSON.stringify(d.prefs)
  const set = (next: Partial<ShippingPrefs>) => setP({ ...p, ...next })
  const setRule = (i: number, r: Partial<ShippingRule>) => set({ services: p.services.map((x, j) => (j === i ? { ...x, ...r } : x)) })
  const setPkg = (i: number, r: Partial<PackageSize>) => set({ packages: p.packages.map((x, j) => (j === i ? { ...x, ...r } : x)) })
  const methods = [...new Set([...d.methods, ...p.services.map((r) => r.match).filter((m) => m !== ANY)])]
  const badPkg = p.packages.some((x) => !x.sku.trim() || !(x.length > 0) || !(x.width > 0) || !(x.height > 0))
  const dupSku = new Set(p.packages.map((x) => x.sku.trim().toLowerCase())).size !== p.packages.length

  return (
    <div className="flex flex-col gap-4 border-t border-ui-border-base px-4 py-4">
      <div>
        <Text size="small" weight="plus">Shipping defaults</Text>
        <Text size="xsmall" className="text-ui-fg-subtle">Orders arrive in ShipStation with this service, ship-from location and package size already chosen. Staff can still change them on a label.</Text>
      </div>
      {d.options_error && <Banner tone="warning" title="Couldn't load carriers and locations from ShipStation">{d.options_error}</Banner>}

      <div className="flex flex-col gap-2">
        <Text size="xsmall" weight="plus">Service</Text>
        {p.services.length === 0 && <Text size="xsmall" className="text-ui-fg-subtle">None set: staff choose the service in ShipStation for each order.</Text>}
        {p.services.map((r, i) => (
          <div key={i} className="grid items-end gap-2 sm:grid-cols-[1fr_1.4fr_auto]">
            <Field label="When the shopper picks">
              <Select value={r.match} disabled={!manage} onValueChange={(v) => setRule(i, { match: v })}>
                <Select.Trigger><Select.Value /></Select.Trigger>
                <Select.Content>
                  {methods.map((m) => <Select.Item key={m} value={m}>{m}</Select.Item>)}
                  <Select.Item value={ANY}>Any other shipping choice</Select.Item>
                </Select.Content>
              </Select>
            </Field>
            <Field label="Ship with">
              <Select
                value={services.some((s) => s.key === svcKey(r)) ? svcKey(r) : undefined}
                disabled={!manage || !services.length}
                onValueChange={(v) => {
                  const s = services.find((x) => x.key === v)
                  if (s) setRule(i, { carrier_code: s.carrier.code, carrier_id: s.carrier.id, service_code: s.service.code, label: s.label })
                }}
              >
                <Select.Trigger><Select.Value placeholder={r.label ?? "Choose a service"} /></Select.Trigger>
                <Select.Content>
                  {services.map((s) => <Select.Item key={s.key} value={s.key}>{s.label}</Select.Item>)}
                </Select.Content>
              </Select>
            </Field>
            {manage && <Button size="small" variant="transparent" onClick={() => set({ services: p.services.filter((_, j) => j !== i) })}>Remove</Button>}
          </div>
        ))}
        {manage && services.length > 0 && (
          <div>
            <Button size="small" variant="secondary" onClick={() => {
              const used = new Set(p.services.map((r) => r.match))
              const s = services.find((x) => x.service.code === "usps_ground_advantage") ?? services[0]
              set({ services: [...p.services, { match: methods.find((m) => !used.has(m)) ?? ANY, carrier_code: s.carrier.code, carrier_id: s.carrier.id, service_code: s.service.code, label: s.label }] })
            }}>Add service rule</Button>
          </div>
        )}
      </div>

      <Field label="Ship from">
        <Select
          value={p.ship_from?.id ?? DEFAULT_FROM}
          disabled={!manage || !d.options}
          onValueChange={(v) => {
            const w = d.options?.warehouses.find((x) => x.id === v)
            set({ ship_from: w ? { id: w.id, label: `${w.name} (${w.address})` } : null })
          }}
        >
          <Select.Trigger><Select.Value /></Select.Trigger>
          <Select.Content>
            <Select.Item value={DEFAULT_FROM}>ShipStation's default location</Select.Item>
            {(d.options?.warehouses ?? []).map((w) => <Select.Item key={w.id} value={w.id}>{`${w.name}${w.address ? ` · ${w.address}` : ""}${w.is_default ? " (default)" : ""}`}</Select.Item>)}
            {p.ship_from && !d.options?.warehouses.some((w) => w.id === p.ship_from!.id) && <Select.Item value={p.ship_from.id}>{p.ship_from.label ?? p.ship_from.id}</Select.Item>}
          </Select.Content>
        </Select>
      </Field>

      <div className="flex flex-col gap-2">
        <Text size="xsmall" weight="plus">Package size by SKU (inches)</Text>
        <Text size="xsmall" className="text-ui-fg-subtle">Sent when an order is exactly one of that item. For a flat mailer use 1 for the height.</Text>
        {p.packages.map((x, i) => (
          <div key={i} className="grid items-end gap-2 grid-cols-[1.6fr_1fr_1fr_1fr_auto]">
            <Field label={i === 0 ? "SKU" : ""}><Input value={x.sku} disabled={!manage} maxLength={100} onChange={(e) => setPkg(i, { sku: e.target.value })} /></Field>
            {(["length", "width", "height"] as const).map((k) => (
              <Field key={k} label={i === 0 ? k[0].toUpperCase() + k.slice(1) : ""}>
                <Input type="number" min={0.1} step={0.1} value={Number.isFinite(x[k]) ? String(x[k]) : ""} disabled={!manage} onChange={(e) => setPkg(i, { [k]: Number(e.target.value) } as Partial<PackageSize>)} />
              </Field>
            ))}
            {manage ? <Button size="small" variant="transparent" onClick={() => set({ packages: p.packages.filter((_, j) => j !== i) })}>Remove</Button> : <span />}
          </div>
        ))}
        {dupSku && <Text size="xsmall" className="text-ui-fg-error">Each SKU can only have one size.</Text>}
        {manage && (
          <div>
            <Button size="small" variant="secondary" onClick={() => set({ packages: [...p.packages, { sku: "", length: 0, width: 0, height: 1 }] })}>Add package size</Button>
          </div>
        )}
      </div>

      {manage && (
        <div className="flex justify-end gap-2">
          {dirty && <Button size="small" variant="secondary" onClick={() => setP(d.prefs)}>Discard</Button>}
          <Button
            size="small"
            disabled={!dirty || badPkg || dupSku}
            isLoading={save.isPending}
            onClick={() => save.mutate({ ...p, packages: p.packages.map((x) => ({ ...x, sku: x.sku.trim() })) }, { onSuccess: () => toast.success("Shipping defaults saved"), onError: (e) => toast.error(errorMessage(e)) })}
          >
            Save shipping defaults
          </Button>
        </div>
      )}
    </div>
  )
}
